import React from 'react';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { Body1, Body5, Container, H3, Page } from '@ui';
import ReactMarkdown from 'react-markdown';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import { TrackingEvent } from '@classes/TrackingEvent';
import { getFromCMSApi } from '@services/api';
import { useParams } from 'react-router';
import { Working } from '@ui/Working';
import { Link } from 'react-router-dom';

const Wrapper = styled.div`
  min-height: 70vh;
  padding: 0 40px 24px 40px;
  margin: 0 auto;
  position: relative;
  max-width: 1440px;

  @media (min-width: 1440px) {
    padding-top: 24px;
  }

  @media (max-width: 768px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: 420px) {
    padding-left: 8px;
    padding-right: 8px;
  }
`;
const LoadingWrapper = styled.div`
  height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;
const BlogPostWrapper = styled.div`
  max-width: 800px;

  h3 {
    line-height: 1.3;
  }

  @media (max-width: 768px) {
    margin: 0 0 0 0;
    h3 {
      line-height: 1.2;
    }
  }

  @media (max-width: 420px) {
  }
`;
const ErrorBody = styled.div`
  height: 60vh;
`;
const ContentBody = styled.div`
  padding: 36px 0 0 0;

  @media (max-width: 768px) {
    margin: 0 0 0 0;
  }
`;

const HeaderContentStyle = styled.div`
  img {
    margin: 24px 0 0 0;
    width: 100%;

    @media (max-width: 768px) {
      width: 100vw;
      margin: 24px -16px 0 -16px;
    }
  }

  a {
    text-decoration: underline;
  }
  a,
  p {
    font-size: 12px;
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }
  p {
    margin: 8px 0 0 0;
  }
`;
const ExtraSectionWrapper = styled.div`
  .border {
    opacity: 0.6;
    margin: 2.6em auto 0 auto;
    border-top: 2px solid ${({ theme }) => theme.themeColors.text};
    //border-top: 2px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    max-width: 10%;
  }
`;
const ContentStyle = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5 {
    letter-spacing: -0.016em;
    font-size: 22px;
    line-height: 28px;
    margin: 3.12em 0 -0.64em 0;
    padding: 0;
  }

  a {
    color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  li,
  p,
  blockquote {
    font-size: 18px;
    line-height: 28px;
  }

  blockquote,
  ul,
  p {
    margin: 2em 0 -0.46em 0;
    padding: 0;
    letter-spacing: -0.002em;
  }

  li {
    margin: 2em 0 -0.46em 0;
    padding: 0;
    letter-spacing: -0.004em;

    font-size: 18px;
    line-height: 24px;
  }

  img {
    margin: 0;
    padding: 0;
    width: 100%;
  }
`;

const HeaderImage = ({ imageData, disclaimer }) => {
  if (!imageData || !imageData.data) {
    return null;
  }

  const {
    data: { attributes },
  } = imageData;
  const {
    url,
    // name,
    // alternativeText,
    // caption,
    // width,
    // height,
    // formats,
    // hash,
    // ext,
    // mime,
    // size,
    // previewUrl,
    // provider,
    // provider_metadata,
    // createdAt,
    // updatedAt,
  } = attributes;
  return (
    <HeaderContentStyle>
      <div>
        <div>
          <img src={url} alt={'header-img'} />
        </div>
        {disclaimer && (
          <div>
            <ReactMarkdown>{disclaimer}</ReactMarkdown>
          </div>
        )}
      </div>
    </HeaderContentStyle>
  );
};

const BlogPost = ({
  content,
  description,
  first_published_at,
  slug,
  title,
  header_disclaimer,
  article_disclaimer,
  author,
  footer_content,
  header,
}) => {
  const timeDisplay = formatLocalizedDateTime('ll2', createTimeInstance(first_published_at));
  return (
    <BlogPostWrapper>
      <Container top={16}>
        <H3 bold>{title}</H3>
      </Container>
      {null && (
        <Container top={0}>
          <Body1 thin>{description}</Body1>
        </Container>
      )}
      <Container top={6} row verticallyCenter>
        {author && <Body5 isLowContrast>{author} -&nbsp;</Body5>}
        <Body5 isLowContrast>{timeDisplay}</Body5>
      </Container>

      <HeaderImage imageData={header} disclaimer={header_disclaimer} />

      <ContentBody>
        <ContentStyle>
          <ReactMarkdown>{content}</ReactMarkdown>
        </ContentStyle>

        {footer_content && (
          <ExtraSectionWrapper>
            <div className={'border'} />
            <ContentStyle>
              <ReactMarkdown>{footer_content}</ReactMarkdown>
            </ContentStyle>
          </ExtraSectionWrapper>
        )}

        {article_disclaimer && (
          <ExtraSectionWrapper>
            <div className={'border'} />

            <ContentStyle>
              <ReactMarkdown>{article_disclaimer}</ReactMarkdown>
            </ContentStyle>
          </ExtraSectionWrapper>
        )}
      </ContentBody>
    </BlogPostWrapper>
  );
};

function PageContainer({ children }) {
  return (
    <>
      <Header noActions />
      <Body>
        <Page width={'1440px'}>{children}</Page>
      </Body>
    </>
  );
}

export default function BlogArticle({ post }) {
  const { pid } = useParams();

  const [blog, setBlog] = React.useState(null);
  const blogPost = post || blog;
  const [isLoading, setIsLoading] = React.useState(!post);
  const [wasError, setWasError] = React.useState(false);

  const getBlogPost = async (id) => {
    const query = `filters[slug][$eqi]=${id}`;
    const data = await getFromCMSApi({ dir: `/blogs`, query: `?populate=*&${query}` });
    return data;
  };

  React.useEffect(() => {
    TrackingEvent.create('View Blog Post', {
      ID: pid,
    });
    const load = async () => {
      const { data, meta, error } = await getBlogPost(pid);
      if (error) {
        setWasError(true);
        setIsLoading(false);
      } else {
        setBlog(data[0]);
        setIsLoading(false);
      }
    };
    if (pid) {
      load();
    }
  }, [pid]);

  if (isLoading) {
    return (
      <PageContainer>
        <Container>
          <LoadingWrapper>
            <Container top={76}>
              <Working />
            </Container>
          </LoadingWrapper>
        </Container>
      </PageContainer>
    );
  }
  if (wasError || !blogPost) {
    return (
      <PageContainer>
        <Container>
          <Link to={'/blog'}>
            <Body5>Back to Blog Home</Body5>
          </Link>
        </Container>
        <ErrorBody>
          <Container top={24}>
            <Body1>Content Not Found...</Body1>
          </Container>
        </ErrorBody>
      </PageContainer>
    );
  }

  const {
    title,
    description,
    content,
    // createdAt,
    // updatedAt,
    // publishedAt,
    slug,
    first_published_at,
    header_disclaimer,
    article_disclaimer,
    author,
    footer_content,
    header,
  } = blogPost.attributes;
  return (
    <PageContainer>
      <Container>
        <Link to={'/blog'}>
          <Body5>Back to Blog Home</Body5>
        </Link>
      </Container>
      <BlogPost
        content={content}
        description={description}
        first_published_at={first_published_at}
        slug={slug}
        title={title}
        header_disclaimer={header_disclaimer}
        article_disclaimer={article_disclaimer}
        author={author}
        footer_content={footer_content}
        header={header}
      />
      <Container top={48} />
    </PageContainer>
  );
}
